import { ChevronDown } from 'lucide-react';
import { useId, useState } from 'react';

import { cn } from '@/shared/lib/cn';

import { CONTROL_BASE } from './control';

interface FacetMultiSelectProps<T extends string> {
  label: string;
  /** Ничего не выбрано — условия нет: «Все жанры». */
  allLabel: string;
  values: readonly T[];
  /** Число у варианта — сколько записей с ним среди прошедших остальные условия. */
  options: readonly { value: T; label: string; count?: number }[];
  onChange: (values: T[]) => void;
}

/**
 * Условие фильтра, где можно выбрать несколько вариантов: «Фантастика» и
 * «Триллер» сразу.
 *
 * Свёрнуто — одной строкой, как выпадающий список `FacetSelect`: «Все жанры»
 * или «Фантастика, Триллер». Раскрыто — варианты с галочками и числами.
 * Нативный `select multiple` для этого не годится: на телефоне это не строка,
 * а список на полэкрана, и какие варианты отмечены, в нём не видно.
 */
export function FacetMultiSelect<T extends string>({
  label,
  allLabel,
  values,
  options,
  onChange,
}: FacetMultiSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const labelId = useId();
  const selected = new Set(values);

  const summary =
    values.length === 0
      ? allLabel
      : options
          .filter((option) => selected.has(option.value))
          .map((option) => option.label)
          .join(', ');

  function toggle(value: T) {
    onChange(selected.has(value) ? values.filter((item) => item !== value) : [...values, value]);
  }

  return (
    <div className="space-y-1.5">
      <p id={labelId} className="text-[13px] font-medium text-muted">
        {label}
      </p>
      <button
        type="button"
        aria-expanded={open}
        aria-label={`${label}: ${summary}`}
        onClick={() => setOpen((value) => !value)}
        className={cn(CONTROL_BASE, 'flex h-11 items-center justify-between gap-2 text-left')}
      >
        <span className="min-w-0 truncate">{summary}</span>
        <ChevronDown
          size={16}
          aria-hidden
          className={cn('shrink-0 text-subtle transition-transform duration-150', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div role="group" aria-labelledby={labelId} className="space-y-0.5">
          <Option label={allLabel} checked={values.length === 0} onChange={() => onChange([])} />
          {options.map((option) => (
            <Option
              key={option.value}
              label={option.count === undefined ? option.label : `${option.label} · ${option.count}`}
              checked={selected.has(option.value)}
              onChange={() => toggle(option.value)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Option({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label className="flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-3 text-[15px] hover:bg-surface-2">
      <input
        type="checkbox"
        className="size-4 shrink-0 accent-[var(--color-brand)]"
        checked={checked}
        onChange={onChange}
      />
      <span className="min-w-0">{label}</span>
    </label>
  );
}
